const settings = require('../settings')
const {
  resources,
  units,
  hoursInDay,
  markupConfig,
  timeUnitKeys,
} = settings

// base selectors
const meta = (resource) => resource.meta || {}
const baseUnit = (resource) => meta(resource).unit
const basePrice = (resource, overrides) => {
  if(overrides && overrides.price) return overrides.price
  return meta(resource).price
}
const baseRate = (resource) => {
  const price = basePrice(resource)
  return price ? {
    price,
    amount: 1,
  } : null
}
const rates = (resource) => meta(resource).rates || []

const baseLine = (resource) => meta(resource).baseline

const linkAmount = (link) => meta(link).amount || 0
const linkPrice = (link) => meta(link).price || 0

const rateUnitPrice = (rate) => {
  if(rate.amount <= 0) return 0
  return rate.priceType == 'total' ?
    rate.price / rate.amount :
    rate.price
}

const rateTotalPrice = (rate) => {
  if(rate.amount <= 0) return 0
  return rate.priceType == 'total' ?
    rate.price :
    rate.price * (rate.amount || 1)
}

const markupTitle = (resourceType) => {
  const resourceDesc = resources[resourceType]
  if(!resourceDesc) console.error(`unknown resource type: ${resourceType}`)
  return resourceDesc.title
}

const markupType = (resourceType) => {
  const resourceMarkupConfig = markupConfig[resourceType]
  return !resourceMarkupConfig || !resourceMarkupConfig.alias ?
    resourceType :
    resourceMarkupConfig.alias
}

const markupIgnore = (resourceType) => {
  const resourceMarkupConfig = markupConfig[resourceType]
  return !resourceMarkupConfig || !resourceMarkupConfig.ignore ?
    false :
    true
}

const markupAmount = ({
  resourceType,
  markups,
  price,
}) => {
  if(markupIgnore(resourceType)) return 0
  if(!markups) return 0
  const markupPercent = markups[markupType(resourceType)]
  if(typeof(markupPercent) != 'number') return 0
  return (markupPercent / 100) * price
}

const markupApply = ({
  resourceType,
  markups,
  price,
}) => {
  if(markupIgnore(resourceType)) return price
  if(!markups) return price

  const totalMarkup = markupAmount({
    resourceType,
    markups,
    price: price.price,
  })

  const baseMarkup = markupAmount({
    resourceType,
    markups,
    price: price.base.price,
  })

  price.price += totalMarkup
  price.base.price += baseMarkup

  return price
}

// descriptors
const getUnitDescriptor = (type) => {
  const unitDesc = units.map[type]
  if(!unitDesc) console.error(`unknown unit type: ${type}`)
  return unitDesc || {}
}

const getResourceDescriptor = (resource) => {
  const resourceDesc = resources[resource.type] || resources.misc
  if(!resourceDesc) console.error(`unknown resource type: ${resource.type}`)
  return resourceDesc || {}
}

// types
const isTimeBased = (resource) => {
  const resourceType = getResourceDescriptor(resource)
  return resourceType.timeBased
}

const isTemplatePriceBased = (resource) => {
  const resourceType = getResourceDescriptor(resource)
  return resourceType.templatePriceBased
}

const isUnitTimeBased = (unit) => {
  return timeUnitKeys.indexOf(unit) >= 0
}

// if the template is not time based and we have time based
// resource then it's amount based
//
// for example - a template that is in m3 and has labour in days
// it's price based
//
// a template that is in days and labour in days
// it's amount based

const getTemplateResourceMode = (resource, templateUnit) => {
  const resourcePriceBased = isTemplatePriceBased(resource)
  const templateTimeBased = isUnitTimeBased(templateUnit)
  if(templateTimeBased) {
    return 'amount'
  } else {
    return resourcePriceBased ?
      'price' :
      'amount'
  }
}

const isBaselineBased = (resource) => {
  const resourceType = getResourceDescriptor(resource)
  return resourceType.baselineBased ? true : false
}

const isLinkBased = (resource) => {
  const resourceType = getResourceDescriptor(resource)
  return resourceType.linkBased ? true : false
}

const isRateBased = (resource) => {
  const resourceType = getResourceDescriptor(resource)
  return resourceType.rateBased ? true : false
}

const isVolumeBased = (resource) => {
  const resourceType = getResourceDescriptor(resource)
  return resourceType.volumeBased ? true : false
}

const hasChildren = (resource) => {
  const resourceType = getResourceDescriptor(resource)
  return resourceType.hasChildren ? true : false
}

// titles
const unitTitle = ({
  amount,
  unit,
}) => {
  const unitDesc = getUnitDescriptor(unit)
  return `${ amount > 1 ? amount : ''} ${unitDesc.title}${ amount != 1 && !unitDesc.disablePluralTitle ? 's' : ''}`
}

const title = (resource) => {
  if(isVolumeBased(resource)) {
    return `${resource.name} (${resource.meta.volume} ${resource.meta.volume_unit})`
  }
  else {
    return resource.name
  }
}

const typeTitle = (resource) => {
  const resourceDesc = getResourceDescriptor(resource)
  return resourceDesc.title
}

const getUnit = (resource) => {
  return isTimeBased(resource) ? 
    'day' :
    baseUnit(resource)
}

// adjust the base amount a unit represents
// if it's a time unit - return the number of days it is
// otherwise the base is 1
const getTimeUnitDays = (unit = 'day') => {
  const unitDesc = getUnitDescriptor(unit)
  if(unitDesc.hours) {
    return (1 / hoursInDay) * unitDesc.hours
  }
  else if(unitDesc.days) {
    return unitDesc.days
  }
}

module.exports = {
  meta,
  baseUnit,
  basePrice,
  baseRate,
  rates,
  baseLine,
  linkAmount,
  linkPrice,
  rateUnitPrice,
  rateTotalPrice,
  getUnitDescriptor,
  getResourceDescriptor,
  isTimeBased,
  isUnitTimeBased,
  getTemplateResourceMode,
  isBaselineBased,
  isLinkBased,
  isRateBased,
  isVolumeBased,
  hasChildren,
  unitTitle,
  typeTitle,
  title,
  getUnit,
  getTimeUnitDays,
  markupType,
  markupIgnore,
  markupAmount,
  markupApply,
  markupTitle,
}